import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu } from "lucide-react";
import { LINKS, LOGO, MAIN_NAV } from "../../data/site";
import MobileMenu from "./MobileMenu";

const childActive = (c, pathname) => pathname === c.to || (c.to.length > 1 && !c.to.includes("#") && pathname.startsWith(`${c.to}/`));

export const isNavActive = (item, pathname) =>
  item.to === "/" ? pathname === "/" : pathname === item.to || !!item.children?.some((c) => childActive(c, pathname));

const linkCls = (active) =>
  `relative inline-flex min-h-[44px] items-center text-sm font-medium transition-colors duration-300 after:absolute after:bottom-2.5 after:left-0 after:h-px after:w-full after:origin-left after:bg-coral-ink after:transition-transform after:duration-500 hover:text-ink hover:after:scale-x-100 ${
    active ? "text-ink after:scale-x-100" : "text-ink/75 after:scale-x-0"
  }`;

const DropItem = ({ item, active, pathname }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const pointer = useRef("");
  useEffect(() => setOpen(false), [pathname]);
  const mouse = (v) => (e) => e.pointerType === "mouse" && setOpen(v);
  return (
    <li
      ref={ref}
      className="relative flex items-center"
      onPointerEnter={mouse(true)}
      onPointerLeave={mouse(false)}
      onBlur={(e) => !ref.current.contains(e.relatedTarget) && setOpen(false)}
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          setOpen(false);
          ref.current.querySelector("button").focus();
        }
      }}
    >
      <Link to={item.to} data-testid={`nav-${item.key}`} aria-current={active ? "page" : undefined} className={linkCls(active)}>
        {item.label}
      </Link>
      <button
        type="button"
        onPointerDown={(e) => (pointer.current = e.pointerType)}
        onClick={() => {
          const mouseClick = pointer.current === "mouse";
          pointer.current = "";
          setOpen((o) => (mouseClick ? true : !o));
        }}
        aria-expanded={open}
        aria-controls={`submenu-${item.key}`}
        aria-label={`${item.label} submenu`}
        data-testid={`nav-${item.key}-submenu-toggle`}
        className="inline-flex h-11 w-7 items-center justify-center text-ink/60 hover:text-ink"
      >
        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={1.75} />
      </button>
      <div
        id={`submenu-${item.key}`}
        data-testid={`nav-${item.key}-submenu`}
        className={`absolute left-1/2 top-full z-10 -translate-x-1/2 pt-2 transition-[opacity,transform,visibility] duration-300 ${open ? "visible translate-y-0 opacity-100" : "invisible -translate-y-1 opacity-0"}`}
      >
        <ul className="min-w-[260px] rounded-[1.25rem] border border-ink/10 bg-ivory/95 p-2 shadow-[0_24px_60px_-30px_rgba(43,38,32,0.5)] backdrop-blur-xl">
          {item.children.map((c) => (
            <li key={c.to + c.label}>
              <Link
                to={c.to}
                data-testid={`nav-sub-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                aria-current={pathname === c.to ? "page" : undefined}
                className={`flex min-h-[44px] items-center justify-between rounded-xl px-4 text-sm transition-colors hover:bg-sand-light ${pathname === c.to ? "font-semibold text-ink" : "text-ink/80"}`}
              >
                {c.label}
                {pathname === c.to && <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-coral" />}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
};

export default function Nav() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-40 px-2 pt-3 min-[380px]:px-3 sm:px-5 sm:pt-4" data-testid="site-header">
        <nav
          aria-label="Main"
          className={`mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 rounded-full border pl-3.5 pr-1.5 backdrop-blur-xl transition-[background-color,box-shadow,border-color] duration-500 sm:pl-6 sm:pr-2.5 xl:h-[70px] ${
            scrolled ? "border-ink/10 bg-ivory/90 shadow-[0_12px_40px_-22px_rgba(43,38,32,0.45)]" : "border-white/50 bg-ivory/80"
          }`}
        >
          <Link to="/" data-testid="nav-logo" className="flex min-h-[44px] shrink-0 items-center" aria-label="Feeling Moved by Jake Ferree — Home">
            <img src={LOGO} alt="Feeling Moved — Body, Mind, Spirit" width="1138" height="183" data-testid="nav-logo-image" className="h-[26px] w-auto max-[359px]:h-[19px] min-[380px]:h-[29px] sm:h-[31px] lg:h-[33px] xl:h-[34px] 2xl:h-[38px]" />
          </Link>

          <ul className="hidden items-center gap-3.5 xl:flex 2xl:gap-5">
            {MAIN_NAV.map((n) =>
              n.children ? (
                <DropItem key={n.key} item={n} active={isNavActive(n, pathname)} pathname={pathname} />
              ) : (
                <li key={n.key}>
                  <NavLink to={n.to} end data-testid={`nav-${n.key}`} className={({ isActive }) => linkCls(isActive)}>
                    {n.label}
                  </NavLink>
                </li>
              )
            )}
          </ul>

          <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5">
            <a
              href={LINKS.login}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-member-login-link"
              className="hidden min-h-[44px] items-center rounded-full border border-ink/20 px-4 text-[13px] font-semibold text-ink transition-colors duration-300 hover:border-ink hover:bg-ink/5 sm:inline-flex"
            >
              Member Login<span className="sr-only"> (opens in a new tab)</span>
            </a>
            <a
              href={LINKS.login}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-member-login-mobile-link"
              aria-label="Member Login (opens in a new tab)"
              className="inline-flex min-h-[44px] items-center rounded-full px-2 text-[12.5px] font-semibold text-ink underline decoration-ink/30 underline-offset-4 transition-colors hover:decoration-ink sm:hidden"
            >
              Log in
            </a>
            <Link
              to={LINKS.contact}
              data-testid="nav-book-session-button"
              className="hidden min-h-[44px] items-center rounded-full bg-coral-ink px-4 text-[13px] font-semibold text-ivory transition-colors duration-300 hover:bg-[#8f3f22] sm:inline-flex 2xl:px-5"
            >
              Book a Session
            </Link>
            <Link to={LINKS.contact} data-testid="nav-book-mobile-button" className="inline-flex min-h-[44px] items-center rounded-full bg-coral-ink px-3 text-[12.5px] font-semibold text-ivory sm:hidden">
              Book
            </Link>
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              aria-controls="mobile-menu"
              data-testid="mobile-menu-toggle-button"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors hover:bg-ink/5 xl:hidden"
            >
              <Menu className="h-6 w-6" strokeWidth={1.5} />
            </button>
          </div>
        </nav>
      </header>
      <MobileMenu open={open} onClose={close} pathname={pathname} />
    </>
  );
}
